import React from "react";

// Public account/data-deletion page (reachable without an account). Google
// Play requires a web URL where users can request account deletion; Apple
// reviewers check for an equivalent path. The retention details below must
// stay in lockstep with Privacy Policy §5A — update both together.
export default function DeleteAccount() {
  return (
    <main className="legalPage">
      <div className="legalPage__inner">
        <h1 className="legalPage__title">Delete Your BetterIntentions Account</h1>
        <p className="legalPage__lead">
          This page explains how to delete your BetterIntentions account and
          the data associated with it. BetterIntentions is operated by
          BetterIntentions, LLC. Account deletion is permanent and
          irreversible — there is no soft delete or deactivation.
        </p>

        <section className="legalPage__section">
          <h2 className="legalPage__sectionTitle">Delete your account in the app (fastest)</h2>
          <div className="legalPage__sectionContent">
            <p>
              Sign in to BetterIntentions (web or mobile app), open{" "}
              <strong>Settings</strong>, and choose <strong>Delete
              Account</strong>. Deletion begins immediately after you confirm.
            </p>
          </div>
        </section>

        <section className="legalPage__section">
          <h2 className="legalPage__sectionTitle">Request deletion by email</h2>
          <div className="legalPage__sectionContent">
            <p>
              If you cannot sign in, email{" "}
              <a href="mailto:privacy@betterintentions.com?subject=Account%20deletion%20request">
                privacy@betterintentions.com
              </a>{" "}
              with the subject line <strong>"Account deletion request"</strong>,
              sent from the email address on your account. We will verify the
              request and complete the deletion within 30 days.
            </p>
          </div>
        </section>

        <section className="legalPage__section">
          <h2 className="legalPage__sectionTitle">What is deleted</h2>
          <div className="legalPage__sectionContent">
            <p>The following is permanently deleted, within seconds of an in-app request:</p>
            <ul>
              <li>Your profile — name, email, phone, demographics, belief attributes, biography</li>
              <li>Your profile picture</li>
              <li>Every conversation you took part in, including the messages on both sides</li>
              <li>Your login credentials</li>
              <li>Your block lists, hidden conversations, read state, and presence</li>
              <li>Your push-notification device tokens</li>
              <li>All reports you filed about other users</li>
            </ul>
          </div>
        </section>

        <section className="legalPage__section">
          <h2 className="legalPage__sectionTitle">What is retained, and for how long</h2>
          <div className="legalPage__sectionContent">
            <ul>
              <li>
                A deletion record — a one-way SHA-256 hash of your email,
                phone, and user ID (no human-readable personal information),
                the IP address used for the deletion request, and the account
                created/deleted dates — kept for 90 days, then automatically
                purged.
              </li>
              <li>
                Reports other users filed about you, kept as anonymized safety
                evidence with your identifier removed and any message snippet
                dropped.
              </li>
              <li>
                Only if your account was disabled for a policy violation
                before deletion: the hashed identifiers above are kept to
                prevent re-registration.
              </li>
              <li>
                Encrypted backup snapshots expire automatically on our normal
                backup rotation (within 90 days).
              </li>
            </ul>
            <p>
              For complete details, see our{" "}
              <a href="/privacy-policy">Privacy Policy</a> (Section 5A).
              Questions? Contact{" "}
              <a href="mailto:privacy@betterintentions.com">privacy@betterintentions.com</a>.
            </p>
          </div>
        </section>
      </div>
    </main>
  );
}
